import React, { useState, useEffect, useRef, useCallback } from 'react';
import InfiniteScroll from 'react-infinite-scroller';
import ProjectClient from '../../models/projects/projectClient';
import Loader from '../common/loader';
import MobileSelector from './mobileSelector';
import ProjectsSelector from './projectsSelector';
import Card from './card';
import PrCard from './prCard';
import type { Project } from '../../models/projects/project';
import type { ProjectsPr } from '../../models/projects/pr';
import { handleAxiosError } from '../../../common/helpers/axiosHelper';
import { hasNextLink } from '../../../common/helpers/linkHeaderHelper';
import { simpleFlashMessage } from '../../../common/utils/flashMessage';

interface Props {
  keyword: string | null;
  order: string | null;
  speficInvestorFlg: boolean;
}

// PRカードの差し込み位置（先頭から数えたカードのindex）。Figmaでは2段目の右=4枚目
const PR_CARD_INDEX = 3;

export default function IndexView({
  keyword: initialKeyword,
  order: initialOrder,
  speficInvestorFlg,
}: Props) {
  const projectClient = useRef(new ProjectClient());
  const [projects, setProjects] = useState<Project[]>([]);
  const [keyword, setKeyword] = useState<string | null>(initialKeyword);
  const [order, setOrder] = useState<string | null>(initialOrder);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [page, setPage] = useState<number>(1);
  const [hasNextPage, setHasNextPage] = useState<boolean>(true);
  const [pr, setPr] = useState<ProjectsPr | null>(null);
  const typingTimeout = useRef<NodeJS.Timeout | null>(null);
  const abortController = useRef<AbortController | null>(null);

  const fetchProjects = useCallback(
    (
      searchKeyword: string | null = keyword,
      searchPage: number = page,
      searchOrder: string | null = order,
    ) => {
      // 直前の未完了リクエストを中断し、結果の競合（race condition）を防ぐ
      abortController.current?.abort();
      const controller = new AbortController();
      abortController.current = controller;
      projectClient.current
        .fetchAll(
          searchPage,
          searchKeyword === '' ? null : searchKeyword,
          searchOrder,
          controller.signal,
        )
        .then(response => {
          const hasNext = hasNextLink(response.headers.link);
          // 1ページ目（新規検索・並び替え）は置換、2ページ目以降（追加読み込み）は追記
          setProjects(prev =>
            searchPage === 1 ? response.data : [...prev, ...response.data],
          );
          setHasNextPage(hasNext);
          setIsLoading(false);
        })
        .catch(error => {
          // 中断されたリクエストは後続のリクエストに引き継ぐため何もしない
          if (controller.signal.aborted) return;
          handleAxiosError(error, simpleFlashMessage);
          // エラー時はローディングを解除しつつ追加読み込みも止め、
          // InfiniteScroll による再取得ループを防ぐ
          setIsLoading(false);
          setHasNextPage(false);
        });
    },
    [page, keyword, order],
  );

  useEffect(() => {
    fetchProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- react-hooks ルール有効化に伴う既存実装の暫定許容（別途対応）
  }, [page, keyword, order]);

  useEffect(() => {
    // アンマウント時に未完了リクエストを中断し、解放後の state 更新を防ぐ
    return () => abortController.current?.abort();
  }, []);

  useEffect(() => {
    // PRカードの表示判定とリンク先を取得する。取得に失敗しても一覧表示には影響させない（カードを出さないだけ）
    const controller = new AbortController();
    projectClient.current
      .fetchPr(controller.signal)
      .then(response => setPr(response.data.pr))
      .catch(() => {
        // アンマウントによる中断時は解放後の state 更新を防ぐため何もしない
        if (controller.signal.aborted) return;
        setPr(null);
      });
    // アンマウント時に未完了リクエストを中断する
    return () => controller.abort();
  }, []);

  const onLoadMore = () => {
    if (!isLoading && hasNextPage) {
      setPage(prev => prev + 1);
      setIsLoading(true);
    }
  };

  const onChangeKeyword = (event: React.ChangeEvent<HTMLInputElement>) => {
    // キーワード変更時はページを1に戻す（前のページを引き継いだまま検索しない）
    setPage(1);
    setKeyword(event.target.value);
  };

  const onClickKeywordSearch = () => {
    if (typingTimeout.current) {
      clearTimeout(typingTimeout.current);
    }
    setProjects([]);
    setPage(1);
    setIsLoading(true);
    setHasNextPage(true);
    setOrder(null);
    typingTimeout.current = setTimeout(() => {
      // クロージャの古い page/order ではなく、1ページ目・並び順リセットを明示的に渡す
      fetchProjects(keyword, 1, null);
      // history.pushState(null, '', `/projects?keyword=${keyword}`);
    }, 1000);
  };

  const onSubmitSearch = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    onClickKeywordSearch();
  };

  const onClickOrder = (orderValue: string | null) => {
    if (!isLoading) {
      setProjects([]);
      setPage(1);
      setIsLoading(true);
      setHasNextPage(true);
      setKeyword('');
      setOrder(orderValue);
    }
  };

  const cards = projects.map(project => (
    <Card
      key={project.id}
      project={project}
      speficInvestorFlg={speficInvestorFlg}
    />
  ));
  if (pr && cards.length > 0) {
    cards.splice(
      Math.min(PR_CARD_INDEX, cards.length),
      0,
      <PrCard key="pr-card" pr={pr} />,
    );
  }

  return (
    <div>
      <div className="row">
        <div className="col d-block d-lg-none mb-10 justify-content-center">
          <MobileSelector
            order={order}
            keyword={keyword}
            onChangeKeyword={onChangeKeyword}
            onClickOrder={onClickOrder}
            onClickKeywordSearch={onClickKeywordSearch}
          />
        </div>
        <div className="col-lg-3 d-none d-lg-block">
          <ProjectsSelector
            order={order}
            keyword={keyword}
            onChangeKeyword={onChangeKeyword}
            onClickOrder={onClickOrder}
            onClickKeywordSearch={onClickKeywordSearch}
            onSubmitSearch={onSubmitSearch}
          />
        </div>
        <div className="col-lg-9 main-row__wrapper">
          <InfiniteScroll
            pageStart={0}
            loadMore={onLoadMore}
            hasMore={hasNextPage}
            loader={<Loader key={0} type="water" />}
            className="row"
          >
            {cards}
          </InfiniteScroll>
          {!isLoading && projects.length === 0 && (
            <div>プロジェクトは見つかりませんでした</div>
          )}
        </div>
        <div className="col d-block d-lg-none">
          <div className="project-selector__attention">
            <span>※</span>
            <span>
              募集ページについては、募集期間終了後、募集の成立不成立に関わらず事業者の要請に応じて非開示となることがあります。ご了承下さい。
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
